import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardFooter,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { RefreshCw } from 'lucide-react';
import Breadcrumbs from '@Components/Breadcrumbs';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCreateActivity } from '@Hooks/useCreateActivity';

const ACTIVITY_TYPES = [
  'Booked Event',
  'Reservation',
  'Ticketed Event',
  'Hotel Stay',
] as const;

const FormSchema = z.object({
  id: z.string().min(1, { message: 'ID is required.' }),
  type: z.enum(ACTIVITY_TYPES, { message: 'Type is required.' }),
  fishId: z.string().uuid({ message: 'Must be a valid UUID.' }),
  location: z.string().optional(),
  space: z.string().optional(),
  phoneNumber: z.string().optional(),
  status: z.string().optional(),
  amount: z.string().optional(),
  eventType: z.string().optional(),
  bookDate: z.string().optional(),
  activityDate: z.string().optional(),
  createdDate: z.string().optional(),
  updatedDate: z.string().optional(),
  fireCreatedTrigger: z.boolean(),
  fireUpdatedTrigger: z.boolean(),
});

type FormValues = z.infer<typeof FormSchema>;

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const toISO = (localValue?: string) =>
  localValue ? new Date(localValue).toISOString() : null;

export default function ActivityTest() {
  const { mutateAsync, isPending } = useCreateActivity();

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      id: crypto.randomUUID(),
      type: 'Booked Event',
      fishId: '',
      location: '',
      space: '',
      phoneNumber: '',
      status: '',
      amount: '',
      eventType: '',
      bookDate: nowLocal(),
      activityDate: nowLocal(),
      createdDate: nowLocal(),
      updatedDate: nowLocal(),
      fireCreatedTrigger: false,
      fireUpdatedTrigger: false,
    },
  });

  const selectedType = form.watch('type');

  const onSubmit = async (values: FormValues) => {
    form.clearErrors('root');

    try {
      const result = await mutateAsync({
        data: {
          id: values.id,
          type: values.type,
          fishId: values.fishId,
          location: values.location || null,
          space: values.space || null,
          phoneNumber: values.phoneNumber || null,
          status: values.status || null,
          amount: values.amount !== '' && values.amount !== undefined ? Number(values.amount) : null,
          eventType: values.eventType || null,
          bookDate: toISO(values.bookDate),
          activityDate: toISO(values.activityDate),
          createdDate: toISO(values.createdDate),
          updatedDate: toISO(values.updatedDate),
        },
        fireCreatedTrigger: values.fireCreatedTrigger,
        fireUpdatedTrigger: values.fireUpdatedTrigger,
      });
      const fired: string[] = result?.meta?.eventsFired ?? [];
      toastSuccess(
        fired.length
          ? `Activity created. Fired: ${fired.join(', ')}`
          : 'Activity created.'
      );
    } catch (error) {
      console.error('Error creating activity:', error);
      form.setError('root', {
        type: 'server',
        message: 'Unable to create activity. Check the console for details.',
      });
    }
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Admin', href: '/' },
          { name: 'Activity Tester', href: '/activity/test' },
        ]}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Heading level="h2">Activity Tester</Heading>
          </div>
          <Card>
            <CardContent>
              {rootError && (
                <div className="mb-4">
                  <Alert variant="destructive">
                    <AlertTitle>Something went wrong</AlertTitle>
                    <AlertDescription>{rootError}</AlertDescription>
                  </Alert>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Activity ID</FormLabel>
                      <div className="flex gap-2">
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          aria-label="Regenerate"
                          title="Regenerate"
                          onClick={() => field.onChange(crypto.randomUUID())}
                        >
                          <RefreshCw className="size-4" />
                        </Button>
                      </div>
                      <FormDescription>
                        Auto-generated UUID. Override to update an existing doc, or click Regenerate for a fresh one.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ACTIVITY_TYPES.map((t) => (
                              <SelectItem key={t} value={t}>
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fishId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fish ID</FormLabel>
                      <FormControl>
                        <Input placeholder="UUID of existing fish" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="space"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Space</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. DEFINITE, CONFIRMED" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Amount</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {selectedType === 'Booked Event' && (
                  <FormField
                    control={form.control}
                    name="eventType"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Event Type</FormLabel>
                        <FormControl>
                          <Input placeholder="Wedding / Social / Corporate / etc." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="bookDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Book Date</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="activityDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Activity Date</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="createdDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Created Date</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="updatedDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Updated Date</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="mt-6 space-y-3">
                <FormField
                  control={form.control}
                  name="fireCreatedTrigger"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="!mt-0">
                        Fire ACTIVITY_CREATED now
                      </FormLabel>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fireUpdatedTrigger"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="!mt-0">
                        Fire ACTIVITY_EVENT now
                      </FormLabel>
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>

            <CardFooter className="flex justify-end">
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Creating…' : 'Create Activity'}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}
